import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useDeviceIntegrations from 'src/DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';
import { IntegratedServiceStatus } from '@ekkogmbh/apisdk';
import SectionDrawerContent from 'src/SettingsSection/Components/SectionDrawerContent';
import { EditOutlined, Refresh } from '@mui/icons-material';
import { getManufacturer, getServiceKnowledge } from '../Helper/integrationKnowledge';
import { ServiceAvatar } from './ServiceAvatar';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { CapabilityChips } from './CapabilityChips';
import useRequest from 'src/Common/Hooks/useRequest';
import { StatusDot } from './StatusDot';
import { keyToLabel } from 'src/Common/textTansformers';
import { StatisticBar } from './StatisticBar';

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant={'caption'}
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

// ---------------------------------------------------------------------------
// Breakdown bar (battery / signal aggregate statistics)
// ---------------------------------------------------------------------------

interface BreakdownSegment {
  label: string;
  value: number;
  color: string;
}

export const BreakdownBar = ({
  title,
  segments,
}: {
  title: string;
  segments: BreakdownSegment[];
}): React.JSX.Element | null => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  if (total === 0) {
    return null;
  }
  return (
    <Stack spacing={0.75}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>
          {title}
        </Typography>
        <Typography variant={'caption'} color={'text.secondary'}>
          {total.toLocaleString()}
        </Typography>
      </Stack>
      <Stack direction={'row'} sx={{ height: 6, borderRadius: 3, overflow: 'hidden' }}>
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <Tooltip key={segment.label} arrow title={`${segment.label}: ${segment.value.toLocaleString()}`}>
              <Box sx={{ flexGrow: segment.value, bgcolor: segment.color, minWidth: 2 }} />
            </Tooltip>
          ))}
      </Stack>
      <Stack direction={'row'} spacing={1.5} flexWrap={'wrap'}>
        {segments.map((segment) => (
          <Stack key={segment.label} direction={'row'} spacing={0.5} alignItems={'center'}>
            <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: segment.color }} />
            <Typography variant={'caption'} color={'text.secondary'}>
              {segment.label} {segment.value.toLocaleString()}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Configuration key/value list (passwords masked)
// ---------------------------------------------------------------------------

// styled typographies
const SECRET_KEY_PATTERN = /password|secret|token|auth/i;
const DetailsKey = ({ value }: { value: string }): React.JSX.Element => (
  <Typography fontSize={15} variant={'caption'} color={'text.secondary'} sx={{ minWidth: 150, flexShrink: 0 }}>
    {keyToLabel(value)}
  </Typography>
);
const DetailsValue = ({ value }: { value: string }): React.JSX.Element => (
  <Typography variant={'body2'} fontSize={13} fontFamily={'monospace'} sx={{ wordBreak: 'break-all' }}>
    {value}
  </Typography>
);

const DetailsKeyValue = ({ label, value }: { label: string; value: unknown }): React.JSX.Element => (
  <Stack direction={'row'} spacing={2} alignItems={'baseline'}>
    <DetailsKey value={label} />
    <DetailsValue value={SECRET_KEY_PATTERN.test(label) ? '••••••••' : String(value)} />
  </Stack>
);

const ConfigurationList = ({ configuration }: { configuration: Record<string, unknown> }): React.JSX.Element => (
  <Stack spacing={0.75}>
    {Object.entries(configuration).map(([key, value], i) => (
      <DetailsKeyValue key={i} label={key} value={value} />
    ))}
  </Stack>
);

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

export const IntegrationDetailsDrawer = ({ onEdit }: { onEdit: () => void }): React.JSX.Element => {
  const { api } = useEkko();
  const { selectedIntegration: integration, descriptions } = useDeviceIntegrations();
  const [isFetchingStatus, startFetchingStatus] = useRequest<IntegratedServiceStatus>();
  const [serviceStatus, setServiceStatus] = useState<IntegratedServiceStatus>();
  const description = useMemo(
    () => descriptions.find((d) => d.name === integration?.serviceName),
    [descriptions, integration?.serviceName],
  );

  const fetchStatus = useCallback(() => {
    if (integration === undefined) {
      return;
    }

    startFetchingStatus(
      () => api.getServiceIntegrationStatus(integration.serviceName, integration.coordinate),
      (result) => setServiceStatus(result),
      () => setServiceStatus(undefined),
    );
  }, [integration, api]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const statusButton = useMemo(
    () => (
      <Stack direction={'row'} spacing={0.5}>
        <StatusDot label={serviceStatus?.status} loading={isFetchingStatus} state={serviceStatus?.status} />
        <IconButton onClick={fetchStatus} disabled={isFetchingStatus}>
          <Refresh fontSize={'small'} />
        </IconButton>
      </Stack>
    ),
    [fetchStatus, isFetchingStatus, serviceStatus?.status],
  );

  const header = useMemo(() => {
    if (integration === undefined) {
      return <></>;
    }

    const { serviceName } = integration;
    const knowledge = getServiceKnowledge(serviceName);
    const manufacturer = knowledge?.manufacturerId ? getManufacturer(knowledge.manufacturerId) : undefined;

    return (
      <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ minWidth: 0 }}>
        {knowledge?.logo ? (
          <Box
            component={'img'}
            src={knowledge.logo}
            alt={knowledge.displayName ?? serviceName}
            sx={{ height: 36, maxWidth: 200, objectFit: 'contain' }}
          />
        ) : (
          <ServiceAvatar serviceName={serviceName} size={40} />
        )}
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          {!knowledge?.logo && (
            <Typography variant={'h6'} fontWeight={700} noWrap>
              {knowledge?.displayName ?? serviceName}
            </Typography>
          )}
          {manufacturer && (
            <Typography variant={'caption'} color={'text.secondary'} noWrap>
              {`by ${manufacturer.name}`}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  }, [integration?.serviceName]);

  /* Device types (per-type capability breakdown) */
  const deviceStack = useMemo(
    () => (
      <Stack spacing={1}>
        <SectionHeader title={'Devices'} />
        <Stack spacing={1}>
          {description?.devices.map((device, idx) => (
            <Stack key={idx} direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={2}>
              <DetailsKey value={device.name} />
              <CapabilityChips capabilities={device.capabilities} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    ),
    [description],
  );

  const configStack = useMemo(
    () => (
      <Stack spacing={1}>
        <SectionHeader title={'Configuration'} />
        <ConfigurationList
          configuration={{
            coordinate: integration?.coordinate,
            ...integration?.configuration,
          }}
        />
      </Stack>
    ),
    [integration?.configuration, integration?.coordinate],
  );

  const statusStack = useMemo(() => {
    if (serviceStatus === undefined) {
      return <></>;
    }

    const stats = serviceStatus.statistics?.map((stat, idx) => (
      <Stack key={'stats_' + idx} direction={'row'} spacing={2} alignItems={'baseline'}>
        <DetailsKey value={stat.name} />
        <StatisticBar statistic={stat} />
      </Stack>
    ));

    const info = Object.entries(serviceStatus.information).map(([key, value], idx) => (
      <DetailsKeyValue key={'info_' + idx} label={key} value={value} />
    ));

    return (
      <Stack spacing={1}>
        <SectionHeader title={'Status'} />
        <Stack direction={'row'} spacing={2} alignItems={'baseline'}>
          <DetailsKey value={'connection'} />
          {statusButton}
        </Stack>
        {serviceStatus?.connectionStatusMessage && (
          <DetailsKeyValue label={'message'} value={serviceStatus.connectionStatusMessage} />
        )}
        {stats}
        {info}
      </Stack>
    );
  }, [statusButton, serviceStatus]);

  return (
    <SectionDrawerContent
      header={header}
      actions={
        integration?.metadata?.env !== true
          ? {
              confirm: {
                onClick: onEdit,
                label: 'Edit',
                icon: <EditOutlined />,
              },
            }
          : undefined
      }
    >
      <Stack spacing={3}>
        {configStack}
        {statusStack}
        {deviceStack}
      </Stack>
    </SectionDrawerContent>
  );
};
